import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/purchasing/components/PurchaseToolbarGlobal';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { RootState } from '@/setup/redux/RootReducer';

import { createItemSupplierOption } from '@/modules/inventory/items/models/ItemModel';

import { createDefaultPurchaseOrderForm } from '../purchasingMockData';
import { getModuleStatuses, StatusManageItem } from '@/modules/settings/redux/SettingsCRUD';
import { bulkPurchaseOrderAction, getPurchaseOrders, getPurchaseOrderDetail, savePurchaseOrder } from '../redux/PurchaseOrderCRUD';
import usePurchasingSuppliers from '../hooks/usePurchasingSuppliers';
import { InventoryPurchaseOrderFormData, InventoryPurchaseOrderRow, PurchaseOrderLine } from '../types';
import PurchaseOrderForm from './PurchaseOrderForm';
import PurchaseOrderList from './PurchaseOrderList';

const buildFormMetadata = (formData: InventoryPurchaseOrderFormData, purchaseOrderId?: string) => ({
  formData,
  purchaseOrderId,
});

const isUuid = (value?: string) =>
  Boolean(value) &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value as string);

export default function PurchasingModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);

  const [purchaseStatuses, setPurchaseStatuses] = useState<StatusManageItem[]>([]);
  const [rows, setRows] = useState<InventoryPurchaseOrderRow[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
  });
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [toolbarFilters, setToolbarFilters] = useState({ company: 'all', status: 'all' });
  const [supplierSearch, setSupplierSearch] = useState('');

  const statusFilterOptions = useMemo(
    () => [{ id: 'all', name: 'Tất cả' }, ...purchaseStatuses.map((s) => ({ id: s.id!, name: s.name }))],
    [purchaseStatuses]
  );

  const [dirtyTabIds, setDirtyTabIds] = useState<Record<number, boolean>>({});

  const selectedCompanyId = useMemo(
    () => String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim(),
    [selectedCompany?.company_id],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, toolbarFilters.company),
    [selectedCompanyId, toolbarFilters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(toolbarFilters.company),
    [toolbarFilters.company],
  );

  const listCompanyId = useMemo(() => {
    if (!activeCompanyFilter || activeCompanyFilter === 'all' || !isUuid(activeCompanyFilter)) {
      return undefined;
    }
    return activeCompanyFilter;
  }, [activeCompanyFilter]);

  const {
    canCreate: canCreatePurchase,
    canUpdate: canUpdatePurchase,
    canDelete: canDeletePurchase,
    resolveTabPermissions,
    resolvePermissionsForCompanyId,
    canReadRecord,
  } = useModuleToolbarPermissions({
    resource: 'inventory.purchase',
    activeCompanyFilter,
    updateTabTypes: ['update-purchase-order'],
  });

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 0, title: 'Danh sách mua hàng', type: 'list', closable: false, createdAt: Date.now() },
    ];
    if (canCreatePurchase) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreatePurchase]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_purchasing_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreatePurchase),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-purchase-order', 'update-purchase-order'],
    allowMultipleTabTypes: ['new-purchase-order', 'update-purchase-order'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId =
        toolbarFilters.company && toolbarFilters.company !== 'all' && toolbarFilters.company !== 'global'
          ? toolbarFilters.company
          : listCompanyId || selectedCompanyId || '';
      const defaultStatusId = purchaseStatuses.find((s) => s.is_default)?.id || '';
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `PO #${currentTabs.filter((tab) => tab.type === 'new-purchase-order').length + 1}`,
        type: 'new-purchase-order',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata(createDefaultPurchaseOrderForm(baseCompanyId, defaultStatusId)),
        },
      };
    },
    onAfterTabChange: () => setSnackbarMessage(null),
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryPurchaseOrderFormData | undefined;
  const activePurchaseOrderId = activeTabData?.metadata?.purchaseOrderId as string | undefined;

  const supplierCompanyId = listCompanyId;

  useEffect(() => {
    getModuleStatuses('purchase').then(setPurchaseStatuses).catch(() => {});
  }, []);

  const { supplierOptions, loading: supplierLoading, addOption: addSupplierOption } = usePurchasingSuppliers({
    companyId: supplierCompanyId,
    search: supplierSearch,
  });

  const handleCreateSupplier = async (data: { name: string; email?: string; phone?: string; address?: any }) => {
    const created = await createItemSupplierOption(data.name, supplierCompanyId || '', {
      email: data.email,
      phone: data.phone,
      address: data.address,
    });
    const option = { id: created.id, name: created.name, code: '' };
    addSupplierOption(option);
    return option;
  };

  const loadPurchaseOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPurchaseOrders({
        page: pagination.page,
        pageSize: pagination.pageSize,
        companyId: listCompanyId,
        search: searchQuery || undefined,
        status: toolbarFilters.status !== 'all' ? toolbarFilters.status : undefined,
      });

      setRows(response.results);
      setPagination((current) => ({
        ...current,
        count: response.count,
        totalPages: response.totalPages,
      }));
    } catch {
      setSnackbarMessage('Lỗi khi tải danh sách PO');
    } finally {
      setLoading(false);
    }
  }, [
    listCompanyId,
    pagination.page,
    pagination.pageSize,
    searchQuery,
    toolbarFilters.status,
  ]);

  useEffect(() => {
    setPagination((current) => ({ ...current, page: 1 }));
  }, [listCompanyId]);

  useEffect(() => {
    loadPurchaseOrders();
  }, [loadPurchaseOrders]);

  const handleToolbarFilterChange = useCallback((field: string, value: string) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setToolbarFilters((current) => ({ ...current, [field]: value }));
  }, []);

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
      setPagination((current) => ({ ...current, page: 1 }));
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
    setPagination((current) => ({ ...current, page: 1 }));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setPagination((current) => ({ ...current, page }));
  };

  const handleApplyBulkAction = async (action: 'approve' | 'delete', selectedRows: InventoryPurchaseOrderRow[]) => {
    if (!selectedRows.length) {
      return;
    }

    if (action === 'approve' && !canUpdatePurchase) {
      setSnackbarMessage('Bạn không có quyền duyệt phiếu mua hàng.');
      return;
    }

    if (action === 'delete' && !canDeletePurchase) {
      setSnackbarMessage('Bạn không có quyền xóa phiếu mua hàng.');
      return;
    }

    setBulkActionLoading(true);
    try {
      const ids = selectedRows.map((row) => row.id);
      const result = await bulkPurchaseOrderAction(ids, action);
      const total = typeof result.total === 'number' ? result.total : ids.length;
      const successCount =
        typeof result.success_count === 'number'
          ? result.success_count
          : Array.isArray(result.results)
            ? result.results.filter((r) => r.success).length
            : total;
      const failedCount =
        typeof result.failed_count === 'number' ? result.failed_count : Math.max(total - successCount, 0);

      if (failedCount > 0) {
        setSnackbarMessage(`Đã xử lý ${successCount}/${total} phiếu. ${failedCount} phiếu lỗi.`);
      } else if (action === 'approve') {
        setSnackbarMessage(`Đã duyệt ${successCount} phiếu mua.`);
      } else {
        setSnackbarMessage(`Đã xóa ${successCount} phiếu mua.`);
      }

      await loadPurchaseOrders();
    } catch {
      setSnackbarMessage('Có lỗi khi thực hiện thao tác hàng loạt');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const updateFormData = (updates: Partial<InventoryPurchaseOrderFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activePurchaseOrderId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activePurchaseOrderId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const openExistingPurchaseOrder = (row: InventoryPurchaseOrderRow) => {
    if (!canReadRecord(row)) {
      setSnackbarMessage('Bạn không có quyền xem chi tiết phiếu mua hàng.');
      return;
    }
    guardAndRun(() => {
      const newTabId = Date.now() + Math.floor(Math.random() * 1000);
      const initialForm: InventoryPurchaseOrderFormData = {
        po_no: row.code,
        purchase_type: row.purchase_type || 'reserve',
        status: row.status,
        company_id: row.company_id,
        company_name: row.company_name,
        supplier_id: row.supplier_id || '',
        supplier_name: row.supplier || '',
        warehouse_id: row.warehouse_id || '',
        warehouse_name: row.warehouse_name || '',
        order_id: null,
        order_no: null,
        material_request_id: row.material_request_id || null,
        material_request_no: row.material_request_no || null,
        requested_by_id: row.requested_by_id || null,
        requested_by_name: row.requested_by_name || null,
        approved_by_id: null,
        approved_by_name: null,
        approved_at: null,
        note: row.note || '',
        lines: [],
      };

      const openedIndex = openTab({
        id: newTabId,
        title: row.code,
        type: 'update-purchase-order',
        closable: true,
        entityId: row.id,
        metadata: {
          companyId: row.company_id,
          ...buildFormMetadata(initialForm, row.id),
        },
      });

      if (openedIndex !== null && openedIndex !== undefined) {
        return;
      }

      (async () => {
        try {
          const detailed = await getPurchaseOrderDetail(row.id);
          updateTab(newTabId, { metadata: buildFormMetadata(detailed, row.id) });
        } catch {
          setSnackbarMessage('Lỗi khi tải chi tiết PO');
        }
      })();
    });
  };

  const updateLineData = (lineId: string, updates: Partial<PurchaseOrderLine>) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activePurchaseOrderId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextLines = activeFormData.lines.map((line) => (line.id === lineId ? { ...line, ...updates } : line));
    const nextFormData = { ...activeFormData, lines: nextLines };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activePurchaseOrderId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const handleSave = async () => {
    if (!activeFormData || !activeTabData?.id) return;

    const isUpdateMode = Boolean(activePurchaseOrderId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage('Bạn không có quyền cập nhật phiếu mua hàng.');
      return;
    }

    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage('Bạn không có quyền tạo phiếu mua hàng.');
      return;
    }

    try {
      const resp = await savePurchaseOrder(activeFormData, activePurchaseOrderId);
      if (resp && resp.id) {
        try {
          const detailed = await getPurchaseOrderDetail(resp.id);
          updateTab(activeTabData.id, {
            title: detailed.po_no || detailed.company_name || `PO ${resp.id}`,
            entityId: resp.id,
            metadata: buildFormMetadata(detailed, resp.id),
          });
        } catch {
          const label = activeFormData.po_no || activeFormData.supplier_name || 'PO';
          updateTab(activeTabData.id, {
            title: label,
            entityId: resp.id,
            metadata: buildFormMetadata(activeFormData, resp.id),
          });
        }
      } else {
        const label = activeFormData.po_no || activeFormData.supplier_name || 'PO';
        updateTab(activeTabData.id, {
          title: label,
          metadata: buildFormMetadata(activeFormData, activePurchaseOrderId),
        });
      }

      handleUnsavedChangesUpdate(false, activeTabData.id);
      setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: false }));
      setSnackbarMessage(`Đã lưu ${activeFormData.po_no || activeFormData.supplier_name || 'PO'}.`);
      await loadPurchaseOrders();
    } catch {
      setSnackbarMessage('Lỗi khi lưu phiếu mua hàng');
    }
  };

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType="inventory"
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryPurchaseOrderFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);

              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((open) => !open)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: toolbarFilters.company || 'all',
                              status: toolbarFilters.status,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={statusFilterOptions}
                          />
                        }
                        dataTable={
                          <PurchaseOrderList
                            rows={rows}
                            loading={loading}
                            pagination={pagination}
                            statusOptions={purchaseStatuses}
                            onPageChange={handlePageChange}
                            onApplyBulkAction={handleApplyBulkAction}
                            bulkActionLoading={bulkActionLoading}
                            onSelectRow={openExistingPurchaseOrder}
                            canUpdate={canUpdatePurchase}
                            canDelete={canDeletePurchase}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <PurchaseOrderForm
                        formData={tabFormData}
                        statusOptions={purchaseStatuses}
                        supplierOptions={supplierOptions}
                        supplierLoading={supplierLoading}
                        onSupplierSearch={setSupplierSearch}
                        onCreateSupplier={handleCreateSupplier}
                        canEdit={tab.type === 'new-purchase-order' ? tabPermissions.canCreate : tabPermissions.canUpdate}
                        onChangeForm={(updates) => {
                          if (tab.id !== activeTabData?.id) {
                            const canEditTab = tab.type === 'new-purchase-order' ? tabPermissions.canCreate : tabPermissions.canUpdate;
                            if (!canEditTab) {
                              return;
                            }
                            updateTab(tab.id, {
                              metadata: buildFormMetadata(
                                { ...tabFormData, ...updates },
                                tab.metadata?.purchaseOrderId as string | undefined
                              ),
                            });
                            handleUnsavedChangesUpdate(true, tab.id);
                            setDirtyTabIds((current) => ({ ...current, [tab.id]: true }));
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onChangeLine={updateLineData}
                        onSave={handleSave}
                        isDirty={Boolean(dirtyTabIds[tab.id])}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert severity="success" onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
